import * as React from 'react';
import {
    Text, TextField, PrimaryButton, DefaultButton, MessageBar, MessageBarType,
    ProgressIndicator, Stack, Spinner, SpinnerSize
} from '@fluentui/react';
import { RouteComponentProps } from 'react-router-dom';
import { BulkOnBehalfCsvRequest, BulkOnBehalfCsvResponse, BulkOnBehalfCsvRowResult, BulkCsvBatchState } from '../../shared/models/UserAccess.model';
import AccessAPI from '../../store/AccessAPI';
import { Utility } from '../../shared/models/Helper';
import ITenant from '../../shared/models/Tenant.model';
import { UIConstants } from '../../shared/models/Constants';

interface IBulkAccessRequestState {
    tenantData: ITenant | null;
    justification: string;
    selectedFile: File | null;
    uploading: boolean;
    batches: BulkCsvBatchState[];
    currentBatch: number;
    totalRows: number;
    aggregatedResults: BulkOnBehalfCsvRowResult[];
    processingComplete: boolean;
    cancelled: boolean;
    showResults: boolean;
    resultsPage: number;
    allDataLines: string[];
    headerLine: string;
    showMessage: boolean;
    message: string;
    messageType: any;
}

class BulkAccessRequest extends React.Component<RouteComponentProps<{}>, IBulkAccessRequestState> {

    private static readonly BULK_CSV_BATCH_SIZE = 100;
    private static readonly BULK_CSV_MAX_RETRIES = 3;
    private static readonly BULK_CSV_RESULTS_PAGE_SIZE = 200;
    private static readonly BULK_CSV_HEADER_INDICATORS = [
        'upn', 'userupn', 'useridentifier', 'user', 'guid',
        'role', 'rolename', 'role_name',
        'businessgroup', 'businessgroupname', 'business_group', 'bg', 'bgname', 'bg_name', 'group'
    ];

    private fileInputRef = React.createRef<HTMLInputElement>();

    constructor(props: RouteComponentProps<{}>) {
        super(props);
        this.state = {
            tenantData: null,
            justification: '',
            selectedFile: null,
            uploading: false,
            batches: [],
            currentBatch: -1,
            totalRows: 0,
            aggregatedResults: [],
            processingComplete: false,
            cancelled: false,
            showResults: false,
            resultsPage: 0,
            allDataLines: [],
            headerLine: '',
            showMessage: false,
            message: '',
            messageType: MessageBarType.info
        };
    }

    componentDidMount() {
        const tenantName = (this.props.match.params as any).tenantName;
        const tenantData = Utility.GetTenantData().filter(x => x.TenantName.toLowerCase() === tenantName.toLowerCase())[0];
        this.setState({ tenantData });
    }

    private onJustificationChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
        if ((newValue || '').length <= 500) {
            this.setState({ justification: newValue || '' });
        }
    }

    private onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0] || null;
        if (file && !file.name.toLowerCase().endsWith('.csv')) {
            this.setState({
                showMessage: true,
                messageType: MessageBarType.error,
                message: 'Please select a CSV file.',
                selectedFile: null
            });
            if (this.fileInputRef.current) this.fileInputRef.current.value = '';
            return;
        }
        if (file && file.size === 0) {
            this.setState({
                showMessage: true,
                messageType: MessageBarType.error,
                message: 'The selected CSV file is empty.',
                selectedFile: null
            });
            if (this.fileInputRef.current) this.fileInputRef.current.value = '';
            return;
        }
        this.setState({ selectedFile: file, showMessage: false });
    }

    private handleUpload = (): void => {
        const { selectedFile } = this.state;
        if (!selectedFile) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const csvContent = e.target?.result as string;
            if (!csvContent || csvContent.trim().length === 0) {
                this.setState({
                    showMessage: true,
                    messageType: MessageBarType.error,
                    message: 'The selected CSV file is empty.'
                });
                return;
            }
            this.parseAndProcessCsvBatches(csvContent);
        };
        reader.readAsText(selectedFile);
    }

    private parseAndProcessCsvBatches = async (csvContent: string): Promise<void> => {
        const lines = csvContent.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
        if (lines.length === 0) {
            this.setState({ showMessage: true, messageType: MessageBarType.error, message: 'CSV file contains no data.' });
            return;
        }

        const firstParts = lines[0].split(',').map(p => p.trim().replace(/^"|"$/g, '').toLowerCase());
        const hasHeader = firstParts.length >= 2
            && BulkAccessRequest.BULK_CSV_HEADER_INDICATORS.includes(firstParts[0])
            && BulkAccessRequest.BULK_CSV_HEADER_INDICATORS.includes(firstParts[1]);

        const headerLine = hasHeader ? lines[0] : '';
        const dataLines = hasHeader ? lines.slice(1) : lines;

        if (dataLines.length === 0) {
            this.setState({ showMessage: true, messageType: MessageBarType.error, message: 'CSV file contains only a header row.' });
            return;
        }

        if (dataLines.length > 1000) {
            this.setState({
                showMessage: true,
                messageType: MessageBarType.warning,
                message: `Large CSV detected (${dataLines.length} rows). Processing in batches of ${BulkAccessRequest.BULK_CSV_BATCH_SIZE}...`
            });
        }

        const batches: BulkCsvBatchState[] = [];
        for (let i = 0; i < dataLines.length; i += BulkAccessRequest.BULK_CSV_BATCH_SIZE) {
            const rowCount = Math.min(BulkAccessRequest.BULK_CSV_BATCH_SIZE, dataLines.length - i);
            batches.push({
                batchIndex: batches.length,
                rowOffset: i,
                rowCount,
                status: 'pending',
                attemptCount: 0,
                results: null,
                error: null
            });
        }

        this.setState({
            uploading: true,
            batches,
            currentBatch: 0,
            totalRows: dataLines.length,
            aggregatedResults: [],
            processingComplete: false,
            cancelled: false,
            showResults: false,
            showMessage: false,
            resultsPage: 0,
            allDataLines: dataLines,
            headerLine
        }, () => this.processBatches(batches));
    }

    private processBatches = async (batches: BulkCsvBatchState[]): Promise<void> => {
        const { tenantData, justification, allDataLines, headerLine } = this.state;
        if (!tenantData) return;

        let aggregatedResults: BulkOnBehalfCsvRowResult[] = [...this.state.aggregatedResults];
        let totalSuccess = aggregatedResults.filter(r => r.success).length;
        let totalFailure = aggregatedResults.filter(r => !r.success).length;

        try {
            for (let i = 0; i < batches.length; i++) {
                const batch = batches[i];
                if (this.state.cancelled) break;
                if (batch.status !== 'pending') continue;

                batch.status = 'processing';
                batch.attemptCount++;
                this.setState({ currentBatch: i, batches: [...batches] });

                try {
                    const chunk = allDataLines.slice(batch.rowOffset, batch.rowOffset + batch.rowCount);
                    const csvContent = headerLine ? [headerLine, ...chunk].join('\n') : chunk.join('\n');

                    const request: any = {
                        tenantId: tenantData.TenantId,
                        tenantName: tenantData.TenantName,
                        scope: tenantData.TenantName,
                        businessJustification: justification || 'Bulk on-behalf CSV upload',
                        csvContent
                    };

                    const result = await AccessAPI.bulkOnBehalfCsvUpload(tenantData.TenantId, request);

                    if ('status' in result && 'message' in result && !('totalRows' in result)) {
                        batch.status = 'failed';
                        batch.error = (result as any).message || 'Request failed';
                        batch.results = null;
                        totalFailure += batch.rowCount;
                    } else {
                        const csvResult = result as BulkOnBehalfCsvResponse;
                        batch.status = 'done';
                        batch.error = null;
                        const adjustedResults = (csvResult.results || []).map(r => ({
                            ...r,
                            rowNumber: r.rowNumber > 0 ? r.rowNumber + batch.rowOffset : r.rowNumber
                        }));
                        batch.results = adjustedResults;
                        totalSuccess += csvResult.successCount;
                        totalFailure += csvResult.failureCount;
                        aggregatedResults = [...aggregatedResults, ...adjustedResults];
                    }
                } catch (ex) {
                    const errorMessage = ex instanceof Error ? ex.message : 'Unexpected error processing batch';
                    console.error(`BulkAccessRequest.processBatches :: Batch ${batch.batchIndex + 1} failed`, ex);
                    batch.status = 'failed';
                    batch.error = errorMessage;
                    batch.results = null;
                    totalFailure += batch.rowCount;
                }

                this.setState({
                    batches: [...batches],
                    aggregatedResults
                });
            }
        } finally {
            const completedBatches = batches.filter(b => b.status === 'done').length;
            const failedBatches = batches.filter(b => b.status === 'failed').length;
            const cancelled = this.state.cancelled;

            this.setState({
                uploading: false,
                selectedFile: null,
                processingComplete: !cancelled,
                currentBatch: -1,
                showResults: true,
                showMessage: true,
                messageType: failedBatches > 0 ? MessageBarType.warning : cancelled ? MessageBarType.warning : MessageBarType.success,
                message: cancelled
                    ? `Cancelled. Processed ${completedBatches} of ${batches.length} batches. ${totalSuccess} succeeded, ${totalFailure} failed.`
                    : `Processed ${this.state.totalRows} rows in ${batches.length} batch${batches.length > 1 ? 'es' : ''}: ${totalSuccess} succeeded, ${totalFailure} failed.${failedBatches > 0 ? ` ${failedBatches} batch(es) failed — use Retry.` : ''}`
            });

            if (this.fileInputRef.current) {
                this.fileInputRef.current.value = '';
            }
        }
    }

    private retryFailedBatches = async (): Promise<void> => {
        const batches = [...this.state.batches];
        const retryable = batches.filter(b => b.status === 'failed' && b.attemptCount < BulkAccessRequest.BULK_CSV_MAX_RETRIES);

        if (retryable.length === 0) {
            this.setState({ showMessage: true, messageType: MessageBarType.warning, message: 'No retryable batches (max retries reached).' });
            return;
        }

        retryable.forEach(b => { b.status = 'pending'; b.error = null; });

        const cleanedResults = this.state.aggregatedResults.filter(r => {
            return !retryable.some(b => r.rowNumber > b.rowOffset && r.rowNumber <= b.rowOffset + b.rowCount);
        });

        this.setState({
            uploading: true,
            batches,
            processingComplete: false,
            cancelled: false,
            aggregatedResults: cleanedResults,
            showMessage: true,
            messageType: MessageBarType.info,
            message: `Retrying ${retryable.length} failed batch${retryable.length > 1 ? 'es' : ''}...`
        }, () => this.processBatches(batches));
    }

    private cancelBulkCsvProcessing = (): void => {
        this.setState({ cancelled: true });
    }

    private downloadTemplate = (): void => {
        const csv = 'UserIdentifier,RoleName,BusinessGroup\nuser@domain.com,Administrators,MyBusinessGroup';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulk-access-request-template.csv';
        a.click();
        URL.revokeObjectURL(url);
    }

    public render(): JSX.Element {
        const {
            justification, selectedFile, uploading, batches, currentBatch,
            aggregatedResults, showResults, showMessage, message, messageType
        } = this.state;

        const completedBatches = batches.filter(b => b.status === 'done' || b.status === 'failed').length;
        const totalBatches = batches.length;
        const failedBatches = batches.filter(b => b.status === 'failed');
        const retryableBatches = failedBatches.filter(b => b.attemptCount < BulkAccessRequest.BULK_CSV_MAX_RETRIES);
        const successSoFar = aggregatedResults.filter(r => r.success).length;
        const failSoFar = aggregatedResults.filter(r => !r.success).length;
        const canUpload = justification.trim().length > 0 && selectedFile !== null && !uploading;

        return (
            <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row" style={{ padding: '20px' }}>
                    <div className="ms-Grid-col ms-sm12 ms-md10 ms-lg8">

                        <Text variant="large" block style={{ fontWeight: 600, marginBottom: '4px' }}>
                            {UIConstants.PageTitle.BulkAccessRequest}
                        </Text>
                        <span style={{ fontSize: '12px', color: '#605e5c', display: 'block', marginBottom: '4px' }}>
                            Upload a CSV file to request roles for multiple users at once. Format: UserIdentifier, RoleName, BusinessGroup (one row per request).
                        </span>
                        <span style={{ fontSize: '12px', color: '#605e5c', display: 'block', marginBottom: '12px' }}>
                            UserIdentifier must be a UPN (user@domain.com). Large files are automatically processed in batches of {BulkAccessRequest.BULK_CSV_BATCH_SIZE}.
                        </span>

                        <MessageBar messageBarType={MessageBarType.info} style={{ marginBottom: '16px' }}>
                            Each row in the CSV will create a separate access request on behalf of the specified user.
                        </MessageBar>

                        {showMessage && (
                            <MessageBar
                                messageBarType={messageType}
                                onDismiss={() => this.setState({ showMessage: false })}
                                style={{ marginBottom: '12px' }}
                            >
                                {message}
                            </MessageBar>
                        )}

                        <TextField
                            label="Business Justification"
                            required
                            multiline
                            rows={3}
                            maxLength={500}
                            value={justification}
                            onChange={this.onJustificationChange}
                            description={`${justification.length}/500 characters`}
                            style={{ marginBottom: '12px' }}
                        />

                        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: '5px' }}>CSV File</label>
                            <input
                                ref={this.fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={this.onFileChange}
                                disabled={uploading}
                            />
                        </div>

                        <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginTop: '12px', marginBottom: '16px' }}>
                            <PrimaryButton
                                text="Upload & Process"
                                onClick={this.handleUpload}
                                disabled={!canUpload}
                                iconProps={{ iconName: 'Upload' }}
                            />
                            <DefaultButton
                                text="Download Template"
                                onClick={this.downloadTemplate}
                                iconProps={{ iconName: 'Download' }}
                            />
                        </Stack>

                        {uploading && totalBatches > 0 && (
                            <div style={{ marginTop: '8px', marginBottom: '10px' }}>
                                <ProgressIndicator
                                    label={`Processing batch ${currentBatch + 1} of ${totalBatches}...`}
                                    description={`${successSoFar} succeeded, ${failSoFar} failed so far`}
                                    percentComplete={totalBatches > 0 ? completedBatches / totalBatches : 0}
                                />
                                <DefaultButton
                                    text="Cancel"
                                    onClick={this.cancelBulkCsvProcessing}
                                    style={{ marginTop: '8px' }}
                                />
                            </div>
                        )}

                        {!uploading && retryableBatches.length > 0 && (
                            <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                                <DefaultButton
                                    text={`Retry ${retryableBatches.length} Failed Batch${retryableBatches.length > 1 ? 'es' : ''}`}
                                    onClick={this.retryFailedBatches}
                                    iconProps={{ iconName: 'Refresh' }}
                                />
                            </div>
                        )}

                        {showResults && aggregatedResults.length > 0 && (
                            <div style={{ marginTop: '10px', maxHeight: '400px', overflowY: 'auto', border: '1px solid #edebe9', padding: '8px' }}>
                                {(() => {
                                    const pageSize = BulkAccessRequest.BULK_CSV_RESULTS_PAGE_SIZE;
                                    const visibleCount = (this.state.resultsPage + 1) * pageSize;
                                    const visibleResults = aggregatedResults.slice(0, visibleCount);
                                    const hasMore = visibleCount < aggregatedResults.length;
                                    return (
                                        <>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                <thead>
                                                    <tr style={{ borderBottom: '2px solid #edebe9', textAlign: 'left' }}>
                                                        <th style={{ padding: '4px 8px' }}>Row</th>
                                                        <th style={{ padding: '4px 8px' }}>User</th>
                                                        <th style={{ padding: '4px 8px' }}>Role</th>
                                                        <th style={{ padding: '4px 8px' }}>Status</th>
                                                        <th style={{ padding: '4px 8px' }}>Message</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleResults.map((r: BulkOnBehalfCsvRowResult) => (
                                                        <tr key={`csv-row-${r.rowNumber}-${r.userIdentifier || ''}-${r.roleName || ''}`} style={{ borderBottom: '1px solid #edebe9' }}>
                                                            <td style={{ padding: '4px 8px' }}>{r.rowNumber}</td>
                                                            <td style={{ padding: '4px 8px' }}>{r.userIdentifier}</td>
                                                            <td style={{ padding: '4px 8px' }}>{r.roleName}</td>
                                                            <td style={{ padding: '4px 8px', color: r.success ? '#107c10' : '#a80000' }}>{r.success ? '✓' : '✗'}</td>
                                                            <td style={{ padding: '4px 8px' }}>{r.message}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {hasMore && (
                                                <div style={{ textAlign: 'center', padding: '8px' }}>
                                                    <DefaultButton
                                                        text={`Show more (${aggregatedResults.length - visibleCount} remaining)`}
                                                        onClick={() => this.setState({ resultsPage: this.state.resultsPage + 1 })}
                                                        styles={{ root: { fontSize: '12px' } }}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        )}

                        {showResults && failedBatches.length > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#a80000' }}>
                                {failedBatches.map(b => (
                                    <div key={`batch-err-${b.batchIndex}`}>
                                        Batch {b.batchIndex + 1} (rows {b.rowOffset + 1}–{b.rowOffset + b.rowCount}): {b.error} {b.attemptCount >= BulkAccessRequest.BULK_CSV_MAX_RETRIES ? '(max retries reached)' : `(attempt ${b.attemptCount}/${BulkAccessRequest.BULK_CSV_MAX_RETRIES})`}
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

export default BulkAccessRequest;
